(() => {
  const APP_PROXY_BASE = '/apps/bike-data';

  // RB filter param keys
  const RB_KEY_MAKE  = 'rb_filter_ptag_d4d4c8481d52898a962340abb9e1459f';
  const RB_KEY_MODEL = 'rb_filter_ptag_284d5cd0dd92c1b4ee0712a15b60ae49';
  const RB_KEY_YEAR  = 'rb_filter_ptag_06f8972cf68b453f544f3dfa58cf0e19';

  const GARAGE_LS_KEY = 'dr_my_garage_v1';
  const BIKE_DATA_CACHE_KEY = 'DR_BIKE_DATA_CACHE_V1';
  const MODEL_RENDER_LIMIT = 200;
  const COMPATIBLE_BIKES_CACHE = new WeakMap();

  // -----------------------------
  // DOM helpers
  // -----------------------------
  function qs(sel, root = document) { return root.querySelector(sel); }
  function qsa(sel, root = document) { return Array.from(root.querySelectorAll(sel)); }
  function setText(el, text) { if (el) el.textContent = text ?? ''; }

  function escapeHtml(input) {
    const s = (input === null || input === undefined) ? '' : String(input);
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }


  function ensureButtonSpinner(btn) {
    if (!btn || btn.querySelector('.loading__spinner')) return;
    const spinnerWrap = document.createElement('span');
    spinnerWrap.className = 'loading__spinner hidden';
    spinnerWrap.setAttribute('aria-hidden', 'true');
    spinnerWrap.innerHTML = '<svg class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg>';
    btn.appendChild(spinnerWrap);
  }

  // Keep button label as-is — just toggle loading class + disabled/spinner
  function setLoading(btn, isLoading) {
    if (!btn) return;
    ensureButtonSpinner(btn);
    btn.disabled = !!isLoading;
    btn.classList.toggle('is-loading', !!isLoading);
    btn.classList.toggle('loading', !!isLoading);
    const spinner = btn.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
  }

  // -----------------------------
  // Cart helpers (Active bike)
  // -----------------------------
  async function getCart() {
    const r = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!r.ok) throw new Error('Failed to fetch cart');
    return r.json();
  }

  async function updateCartAttributes(attrs) {
    const form = new FormData();
    Object.entries(attrs).forEach(([k, v]) => {
      form.append(`attributes[${k}]`, v == null ? '' : String(v));
    });

    const r = await fetch('/cart/update.js', {
      method: 'POST',
      body: form,
      credentials: 'same-origin'
    });

    if (!r.ok) throw new Error('Failed to update cart attributes');
    return r.json();
  }

  async function getActiveBike() {
    const cart = await getCart();
    const a = cart.attributes || {};
    const bike = {
      bike_uvc: a.bike_uvc || '',
      bike_make: a.bike_make || '',
      bike_model: a.bike_model || '',
      bike_year: a.bike_year || '',
      clean_display_name: a.clean_display_name || ''
    };
    const hasBike = !!(bike.bike_make && bike.bike_model && bike.bike_year);
    return { hasBike, bike };
  }

  function formatBikeName(bike) {
    if (!bike) return '';
    return `${bike.bike_make || ''} ${bike.bike_model || ''} ${bike.bike_year || ''}`.trim();
  }

  function normalizeMakeKey(make) {
    return String(make || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');
  }

  // -----------------------------
  // Saved bikes (localStorage)
  // -----------------------------
  function loadGarage() {
    try {
      const raw = localStorage.getItem(GARAGE_LS_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  }

  function saveGarage(list) {
    try { localStorage.setItem(GARAGE_LS_KEY, JSON.stringify(list)); } catch {}
  }

  function upsertGarageBike(bike) {
    const list = loadGarage();
    const id = `${bike.bike_uvc || ''}__${bike.bike_make}__${bike.bike_model}__${bike.bike_year}`;
    const next = [
      { ...bike, _id: id, _ts: Date.now() },
      ...list.filter(x => x._id !== id)
    ].slice(0, 10);
    saveGarage(next);
    return next;
  }

  function removeGarageBike(id) {
    const next = loadGarage().filter(x => x._id !== id);
    saveGarage(next);
    return next;
  }

  function bikeIdentityKey(bike) {
    if (!bike) return '';
    if (bike._id) return String(bike._id);
    return `${bike.bike_uvc || ''}__${bike.bike_make || ''}__${bike.bike_model || ''}__${bike.bike_year || ''}`;
  }

  function getBrandLogoUrl(make) {
    const logos = window.DR_BRAND_LOGOS || {};
    const key = normalizeMakeKey(make);
    return logos[key] || '';
  }

  function getGarageLabel(modal, key, fallback) {
    const value = modal?.dataset?.[key];
    return value && String(value).trim() ? value : fallback;
  }

  function getMakeInitials(make) {
    const parts = String(make || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }

  function buildGarageRow(item, modal, opts = {}) {
    const { isActive = false, onShop } = opts;
    const row = document.createElement('div');
    row.className = `drg__row${isActive ? ' is-active-row' : ''}`;
    row.dataset.garageItemId = item._id || bikeIdentityKey(item);

    const brand = document.createElement('div');
    brand.className = 'drg__brand';

    const logoUrl = getBrandLogoUrl(item.bike_make);
    if (logoUrl) {
      const img = document.createElement('img');
      img.className = 'drg__brandImg';
      img.src = logoUrl;
      img.alt = item.bike_make || '';
      img.loading = 'lazy';
      img.width = 32;
      img.height = 32;
      brand.appendChild(img);
    } else {
      const fallback = document.createElement('span');
      fallback.className = 'drg__brandFallback';
      fallback.textContent = getMakeInitials(item.bike_make);
      brand.appendChild(fallback);
    }

    const body = document.createElement('div');
    body.className = 'drg__rowBody';

    if (isActive) {
      const activeLabel = document.createElement('div');
      activeLabel.className = 'drg__activeLabel';
      const activeLabelText = getGarageLabel(modal, 'activeLabelText', 'Active');
      activeLabel.innerHTML = `<span class="drg__activeTick" aria-hidden="true"><svg viewBox="0 0 24 24" focusable="false"><path d="M5 12.5l4.2 4.2L19 7" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/></svg></span>${escapeHtml(activeLabelText)}`;
      body.appendChild(activeLabel);
    }

    const name = document.createElement('div');
    name.className = 'drg__name';
    name.textContent = formatBikeName(item);
    body.appendChild(name);

    const actions = document.createElement('div');
    actions.className = 'drg__actions';

    if (isActive) {
      const shopBtn = document.createElement('button');
      shopBtn.type = 'button';
      shopBtn.className = 'drg__btn button button--small';
      shopBtn.textContent = getGarageLabel(modal, 'activeRowButtonLabel', 'Shop this bike');
      shopBtn.addEventListener('click', async () => {
        if (typeof onShop === 'function') {
          await onShop(item);
        }
      });
      actions.appendChild(shopBtn);
    } else {
      const useBtn = document.createElement('button');
      useBtn.type = 'button';
      useBtn.className = 'drg__btn button button--secondary button--small';
      useBtn.textContent = getGarageLabel(modal, 'useButtonLabel', 'Use');
      useBtn.addEventListener('click', async () => {
        await applyBikeAndGo(item);
      });
      actions.appendChild(useBtn);
    }

    if (item._id) {
      const delBtn = document.createElement('button');
      delBtn.type = 'button';
      delBtn.className = 'drg__iconBtn drg__removeBtn';
      const removePrefix = getGarageLabel(modal, 'removeAriaPrefix', 'Remove saved bike:');
      delBtn.setAttribute('aria-label', `${removePrefix} ${formatBikeName(item)}`);
      delBtn.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6L18 18M18 6L6 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
      delBtn.addEventListener('click', async () => {
        row.classList.add('is-removing');
        const motionMs = parseInt(getComputedStyle(modal).getPropertyValue('--dr-motion-duration'), 10) || 220;
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const waitMs = reducedMotion ? 1 : motionMs;

        window.setTimeout(async () => {
          removeGarageBike(item._id);
          if (isActive) {
            await clearActiveBike();
          }
          updateGarageCountBubbles();
          await renderGarage(modal);
        }, waitMs);
      });
      actions.appendChild(delBtn);
    }

    row.appendChild(brand);
    row.appendChild(body);
    row.appendChild(actions);

    return row;
  }

  // -----------------------------
  // Header bubbles
  // -----------------------------
  async function updateGarageCountBubbles() {
    let count = 0;

    try {
      const list = JSON.parse(localStorage.getItem(GARAGE_LS_KEY) || '[]');
      count = Array.isArray(list) ? list.length : 0;
    } catch {}

    qsa('[data-garage-count]').forEach(el => {
      el.textContent = String(count);
    });

    qsa('[data-garage-count-bubble]').forEach(b => {
      b.style.display = count > 0 ? '' : 'none';
    });
  }
  window.updateGarageCountBubbles = updateGarageCountBubbles;

  // -----------------------------
  // Excluded collections (metafield flag injected in theme.liquid)
  // -----------------------------
  function isCollectionExcludedFromBikeFiltering() {
    const v = window.DR_EXCLUDE_COLLECTION_FROM_BIKE_SEARCH;
    return v === true || v === 'true' || v === 1 || v === '1';
  }

  function hideBikeFinderIfExcluded() {
    const path = window.location.pathname || '';
    if (!path.startsWith('/collections/')) return;
    if (!isCollectionExcludedFromBikeFiltering()) return;
    qsa('[data-dr-bike-finder]').forEach(el => { el.style.display = 'none'; });
    qsa('[data-dr-fitment-banner]').forEach(el => { el.style.display = 'none'; });
  }

  // -----------------------------
  // RB URL builders
  // -----------------------------
  function buildUrlWithBikeParams(basePath, make, model, year) {
    const u = new URL(window.location.origin + basePath);
    u.searchParams.set('page', '1');
    u.searchParams.set('scrollTo', 'collection-content-container');
    u.searchParams.set(RB_KEY_MAKE, make);
    u.searchParams.set(RB_KEY_MODEL, model);
    u.searchParams.set(RB_KEY_YEAR, String(year));
    return u.pathname + u.search;
  }

  function hasRbBikeFilters(u) {
    return u.searchParams.has(RB_KEY_MAKE) && u.searchParams.has(RB_KEY_MODEL) && u.searchParams.has(RB_KEY_YEAR);
  }

  function stripRbParamsFromUrl(urlString) {
    const u = new URL(urlString, window.location.origin);
    u.searchParams.delete(RB_KEY_MAKE);
    u.searchParams.delete(RB_KEY_MODEL);
    u.searchParams.delete(RB_KEY_YEAR);
    u.searchParams.delete('scrollTo');
    u.searchParams.delete('page');
    return u.pathname + u.search;
  }

  function defaultRedirectCollectionPath() {
    const p = window.location.pathname || '';
    if (p.startsWith('/collections/') && !isCollectionExcludedFromBikeFiltering()) return p;
    return '/collections/all';
  }

  function isCurrentPdpBikeSpecific() {
    const pathname = window.location.pathname || '';
    if (!pathname.startsWith('/products/')) return false;

    const el = document.querySelector('[data-dr-pdp-fitment]');
    if (!el) return false;

    return String(el.getAttribute('data-bike-specific')) === 'true';
  }

  function readCookie(name) {
    const needle = `${name}=`;
    const part = (document.cookie || '').split(';').map((v) => v.trim()).find((v) => v.startsWith(needle));
    return part ? decodeURIComponent(part.slice(needle.length)) : '';
  }

  function resolveBikeRedirectPath() {
    const fromCookie = readCookie('redirect_url');
    if (fromCookie && fromCookie.startsWith('/')) {
      return stripRbParamsFromUrl(fromCookie);
    }
    return defaultRedirectCollectionPath();
  }

  // -----------------------------
  // API calls + finder caching
  // -----------------------------
  const bikeDataCache = {
    makes: null,
    modelsByMake: new Map(),
    yearsByMakeModel: new Map(),
    modelIndexByMake: new Map(),
    makeIndex: null,
    yearIndexByMakeModel: new Map(),
    makeOptionsHtml: '',
    modelOptionsHtmlByMake: new Map(),
    yearOptionsHtmlByMakeModel: new Map(),
    yearOptionsHtmlByKey: new Map()
  };

  function normalizeQuery(input) {
    return String(input || '').toUpperCase().replace(/[\s\-./]+/g, '');
  }

  function normalizeForTokens(input) {
    return String(input || '')
      .trim()
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^A-Z0-9]+/g, ' ')
      .trim();
  }

  function buildTokens(input) {
    const norm = normalizeForTokens(input);
    if (!norm) return new Set();

    const tokens = new Set();
    norm.split(/\s+/).forEach((part) => {
      if (!part) return;
      tokens.add(part);
      const chunks = part.match(/[A-Z]+|\d+/g) || [];
      if (chunks.length > 1) {
        chunks.forEach((chunk) => tokens.add(chunk));
        for (let i = 0; i < chunks.length - 1; i += 1) {
          tokens.add(chunks[i] + chunks[i + 1]);
        }
      }
    });

    return tokens;
  }

  function scoreByTokens(queryTokens, candidateTokens) {
    if (!queryTokens || !queryTokens.size) return 0;

    const numericTokens = [];
    queryTokens.forEach((token) => {
      if (/^\d+$/.test(token)) numericTokens.push(token);
    });

    if (numericTokens.length) {
      const candidateNumericTokens = [];
      candidateTokens.forEach((token) => {
        if (/^\d+$/.test(token)) candidateNumericTokens.push(token);
      });

      const hasAllNumeric = numericTokens.every((token) => {
        if (candidateTokens.has(token)) return true;
        if (token.length < 2) return false;
        return candidateNumericTokens.some((candidateToken) => candidateToken.startsWith(token));
      });
      if (!hasAllNumeric) return -1e9;
    }

    let score = numericTokens.length ? 50 : 0;
    let hits = 0;
    queryTokens.forEach((token) => {
      if (candidateTokens.has(token)) hits += 1;
    });

    if (hits === queryTokens.size) score += 40;
    score += hits * 10;
    score -= Math.max(0, candidateTokens.size - queryTokens.size);
    return score;
  }

  function buildOptionIndex(values = []) {
    return values.map((v) => {
      const label = String(v);
      return {
        label,
        value: label,
        norm: normalizeQuery(label),
        tokens: buildTokens(label)
      };
    });
  }

  function buildComboOptionsHtml(values = []) {
    return (values || []).slice(0, MODEL_RENDER_LIMIT).map((v, idx) => {
      const value = String(v);
      return `<li role="option" class="drbf__comboOption" aria-selected="false" data-idx="${idx}" data-value="${value.replace(/"/g, '&quot;')}">${escapeHtml(value)}</li>`;
    }).join('');
  }

  function debounce(fn, wait = 60) {
    let t = null;
    return (...args) => {
      if (t) window.clearTimeout(t);
      t = window.setTimeout(() => fn(...args), wait);
    };
  }

  function loadBikeSessionCache() {
    try {
      const raw = sessionStorage.getItem(BIKE_DATA_CACHE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return;
      bikeDataCache.makes = Array.isArray(parsed.makes) ? parsed.makes : null;
      const models = parsed.modelsByMake || {};
      const years = parsed.yearsByMakeModel || {};
      Object.keys(models).forEach((k) => {
        const values = Array.isArray(models[k]) ? models[k] : [];
        bikeDataCache.modelsByMake.set(k, values);
        bikeDataCache.modelOptionsHtmlByMake.set(k, buildComboOptionsHtml(values));
      });
      Object.keys(years).forEach((k) => {
        const values = Array.isArray(years[k]) ? years[k] : [];
        bikeDataCache.yearsByMakeModel.set(k, values);
        const html = buildComboOptionsHtml(values);
        bikeDataCache.yearOptionsHtmlByMakeModel.set(k, html);
        bikeDataCache.yearOptionsHtmlByKey.set(k.replace('|', '|||'), html);
      });
      bikeDataCache.makeOptionsHtml = buildComboOptionsHtml(bikeDataCache.makes || []);
    } catch {}
  }

  function persistBikeSessionCache() {
    try {
      const modelsByMake = {};
      const yearsByMakeModel = {};
      bikeDataCache.modelsByMake.forEach((v, k) => { modelsByMake[k] = v; });
      bikeDataCache.yearsByMakeModel.forEach((v, k) => { yearsByMakeModel[k] = v; });
      sessionStorage.setItem(BIKE_DATA_CACHE_KEY, JSON.stringify({
        makes: bikeDataCache.makes || [],
        modelsByMake,
        yearsByMakeModel
      }));
    } catch {}
  }

  function modelIndexForMake(make, models = []) {
    const key = String(make || '');
    if (!key) return [];
    const current = bikeDataCache.modelIndexByMake.get(key);
    if (current && current.length === models.length) return current;
    const next = buildOptionIndex(models);
    bikeDataCache.modelIndexByMake.set(key, next);
    return next;
  }

  function yearIndexForMakeModel(make, model, years = []) {
    const key = `${String(make || '')}|${String(model || '')}`;
    const current = bikeDataCache.yearIndexByMakeModel.get(key);
    if (current && current.length === years.length) return current;
    const next = buildOptionIndex(years);
    bikeDataCache.yearIndexByMakeModel.set(key, next);
    return next;
  }

  async function apiGet(path, params = {}) {
    const u = new URL(APP_PROXY_BASE + '/' + path, window.location.origin);
    Object.entries(params).forEach(([k, v]) => u.searchParams.set(k, v));
    const r = await fetch(u.toString(), { credentials: 'same-origin' });
    if (!r.ok) throw new Error(`API GET failed: ${path}`);
    return r.json();
  }

  async function fetchMakesCached() {
    if (bikeDataCache.makes) return bikeDataCache.makes;
    const r = await apiGet('makes');
    bikeDataCache.makes = r.values || [];
    bikeDataCache.makeOptionsHtml = buildComboOptionsHtml(bikeDataCache.makes);
    persistBikeSessionCache();
    return bikeDataCache.makes;
  }

  async function fetchModelsCached(make) {
    const key = String(make || '');
    if (!key) return [];
    if (bikeDataCache.modelsByMake.has(key)) return bikeDataCache.modelsByMake.get(key) || [];
    const r = await apiGet('models', { make: key });
    const values = r.values || [];
    bikeDataCache.modelsByMake.set(key, values);
    bikeDataCache.modelOptionsHtmlByMake.set(key, buildComboOptionsHtml(values));
    modelIndexForMake(key, values);
    persistBikeSessionCache();
    return values;
  }

  async function fetchYearsCached(make, model) {
    const makeKey = String(make || '');
    const modelKey = String(model || '');
    const key = `${makeKey}|${modelKey}`;
    if (!makeKey || !modelKey) return [];
    if (bikeDataCache.yearsByMakeModel.has(key)) return bikeDataCache.yearsByMakeModel.get(key) || [];
    const r = await apiGet('years', { make: makeKey, model: modelKey });
    const values = (r.values || []).map(String);
    const html = buildComboOptionsHtml(values);
    bikeDataCache.yearsByMakeModel.set(key, values);
    bikeDataCache.yearOptionsHtmlByMakeModel.set(key, html);
    bikeDataCache.yearOptionsHtmlByKey.set(`${makeKey}|||${modelKey}`, html);
    persistBikeSessionCache();
    return values;
  }

  async function apiPostJson(path, body) {
    const u = new URL(APP_PROXY_BASE + '/' + path, window.location.origin);
    const r = await fetch(u.toString(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {})
    });
    if (!r.ok) throw new Error(`API POST failed: ${path}`);
    return r.json();
  }

  // -----------------------------
  // VRM formatting (allow 7 + space in UI, normalise for API)
  // -----------------------------
  function normalizeVrm(raw) {
    return String(raw || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  }

  function formatVrmDisplay(raw) {
    const norm = normalizeVrm(raw).slice(0, 7);
    if (!norm) return '';
    if (norm.length > 3) return (norm.slice(0, norm.length - 3) + ' ' + norm.slice(-3)).slice(0, 8);
    return norm.slice(0, 8);
  }

  function sanitizeVrmTyping(raw) {
    return String(raw || '')
      .toUpperCase()
      .replace(/[^A-Z0-9 ]/g, '')
      .slice(0, 8);
  }

  loadBikeSessionCache();

  // -----------------------------
  // Apply bike helpers
  // -----------------------------
  async function applyBike(bike) {
    await updateCartAttributes({
      bike_uvc: bike.bike_uvc || '',
      bike_make: bike.bike_make,
      bike_model: bike.bike_model,
      bike_year: bike.bike_year,
      clean_display_name: bike.clean_display_name || `${bike.bike_make} ${bike.bike_model} ${bike.bike_year}`
    });

    upsertGarageBike(bike);
    updateGarageCountBubbles();
    document.dispatchEvent(new CustomEvent('dr:bikeChanged', {
      detail: { hasBike: true, bike }
    }));
  }

  async function clearActiveBike() {
    await updateCartAttributes({
      bike_uvc: '',
      bike_make: '',
      bike_model: '',
      bike_year: '',
      clean_display_name: ''
    });
    document.dispatchEvent(new CustomEvent('dr:bikeChanged', {
      detail: { hasBike: false }
    }));
  }

  async function applyBikeAndGo(bike, redirectCollectionPath) {
    await applyBike(bike);

    if (!redirectCollectionPath && (window.location.pathname || '').startsWith('/products/')) {
      if (isCurrentPdpBikeSpecific()) {
        window.location.reload();
        return;
      }

      const url = buildUrlWithBikeParams('/collections/all', bike.bike_make, bike.bike_model, bike.bike_year);
      window.location.assign(url);
      return;
    }

    const basePath = redirectCollectionPath || defaultRedirectCollectionPath();
    const url = buildUrlWithBikeParams(basePath, bike.bike_make, bike.bike_model, bike.bike_year);
    window.location.assign(url);
  }

  // -----------------------------
  // Fitment banner (collections)
  // -----------------------------
  async function initFitmentBanner() {
    const banner = document.querySelector('[data-dr-fitment-banner]');
    if (!banner) return;
    if (!(window.location.pathname || '').startsWith('/collections/')) return;
    if (isCollectionExcludedFromBikeFiltering()) {
      banner.style.display = 'none';
      return;
    }

    const labelEl   = banner.querySelector('[data-dr-fitment-label]');
    const labelTextEl = labelEl ? (labelEl.querySelector('[data-dr-fitment-label-text]') || labelEl) : null;
    const bikeEl    = banner.querySelector('[data-dr-fitment-bike]');
    const imgEl     = banner.querySelector('[data-dr-fitment-brand-img]');
    const changeBtn = banner.querySelector('[data-dr-fitment-change]');
    const chooseBtn = banner.querySelector('[data-dr-fitment-choose]');
    const viewAllBtn = banner.querySelector('[data-dr-fitment-view-all]');
    const brandWrap = banner.querySelector('[data-dr-fitment-brand]');
    const helperEl  = banner.querySelector('[data-dr-fitment-helper]');

    // IMPORTANT: do not force-show buttons on load.
    // Default HTML should be: Choose visible, Change hidden.

    banner.setAttribute('data-sbb-loading', 'true');

    try {
      const { hasBike, bike } = await getActiveBike();

      const hasDefaultTitle = banner.hasAttribute('data-text-default-title');
      const defaultTitle = hasDefaultTitle ? banner.dataset.textDefaultTitle : '';
      const hasDefaultSubtitle = banner.hasAttribute('data-text-default-subtitle');
      const defaultSubtitle = hasDefaultSubtitle ? banner.dataset.textDefaultSubtitle : '';
      const hasActiveTitle = banner.hasAttribute('data-text-active-title');
      const activeTitle = hasActiveTitle ? banner.dataset.textActiveTitle : '';
      const hasSuffix = banner.hasAttribute('data-text-active-platform-suffix');
      const platformSuffix = hasSuffix ? banner.dataset.textActivePlatformSuffix : '';
      const path = window.location.pathname || '';
      const isAll = path === '/collections/all' || path.startsWith('/collections/all/');

      // Toggle CTAs once we know the state (prevents "both buttons then hide one")
      if (chooseBtn) chooseBtn.hidden = !!hasBike;
      if (changeBtn) changeBtn.hidden = !hasBike;
      if (helperEl) helperEl.hidden = !hasBike;

      if (!hasBike) {
        banner.classList.remove('has-bike');
        if (labelTextEl) labelTextEl.textContent = defaultTitle;
        if (bikeEl)  bikeEl.textContent  = defaultSubtitle;
        if (brandWrap) brandWrap.hidden = true;
        if (helperEl) helperEl.hidden = true;
        if (viewAllBtn) viewAllBtn.hidden = true;
        return;
      }

      banner.classList.add('has-bike');

      // Has bike
      if (labelTextEl) labelTextEl.textContent = activeTitle;
      if (bikeEl)  bikeEl.textContent  = `${formatBikeName(bike)} ${platformSuffix}`.trim();
      if (viewAllBtn) viewAllBtn.hidden = isAll;

      // Brand logo
      const logos = window.DR_BRAND_LOGOS || {};
      const key = normalizeMakeKey(bike.bike_make);
      const url = logos[key];

      if (brandWrap && imgEl && url) {
        imgEl.src = url;
        imgEl.alt = bike.bike_make || '';
        brandWrap.hidden = false;
      } else if (brandWrap) {
        brandWrap.hidden = true;
      }

    } catch {
      const hasDefaultTitle = banner.hasAttribute('data-text-default-title');
      const defaultTitle = hasDefaultTitle ? banner.dataset.textDefaultTitle : '';
      const hasDefaultSubtitle = banner.hasAttribute('data-text-default-subtitle');
      const defaultSubtitle = hasDefaultSubtitle ? banner.dataset.textDefaultSubtitle : '';

      // On error, fall back to default "no bike" UI
      banner.classList.remove('has-bike');
      if (labelTextEl) labelTextEl.textContent = defaultTitle;
      if (bikeEl)  bikeEl.textContent  = defaultSubtitle;

      if (chooseBtn) chooseBtn.hidden = false;
      if (changeBtn) changeBtn.hidden = true;
      if (brandWrap) brandWrap.hidden = true;
      if (helperEl) helperEl.hidden = true;
      if (viewAllBtn) viewAllBtn.hidden = true;
    } finally {
      banner.removeAttribute('data-sbb-loading');
    }
  }

  async function initHomeFitmentConfirm() {
    // 1) Preferred / legacy home widget (if you have it anywhere)
    const legacy = document.querySelector('[data-home-fitment-confirm]');
    if (legacy) {
      const bikeNameEl = legacy.querySelector('[data-home-bike-name]');
      const changeBtn = legacy.querySelector('[data-home-fitment-change]');
      const viewAll = legacy.querySelector('[data-home-fitment-viewall]');

      if (changeBtn && changeBtn.dataset.homeFitmentBound !== '1') {
        changeBtn.dataset.homeFitmentBound = '1';
        changeBtn.addEventListener('click', () => {
          document.dispatchEvent(new CustomEvent('dr:openGarage', { bubbles: true }));

          if (!window.__drGarageEventBound) {
            const opener = document.querySelector(
              '[data-open-garage], [data-dr-open-garage], [data-garage-open], a.header-my-garage, .header-my-garage, a[href="#my-garage"], a[href="#garage"]'
            );
            if (opener && opener !== changeBtn) opener.click();
          }
        });
      }

      const fallbackUrl = legacy.dataset.fallbackUrl || '/collections/all';
      const showBikeName = legacy.dataset.showBikeName === 'true';

      try {
        const { hasBike, bike } = await getActiveBike();
        if (!hasBike) {
          legacy.hidden = true;
          if (bikeNameEl) bikeNameEl.textContent = '';
          if (viewAll) viewAll.href = fallbackUrl;
          return;
        }

        legacy.hidden = false;

        const displayName = (bike.clean_display_name || formatBikeName(bike) || '').trim();
        if (bikeNameEl) {
          bikeNameEl.textContent = (showBikeName && displayName) ? ` for your ${displayName}` : '';
        }

        const redirectBase = resolveBikeRedirectPath();
        const redirectUrl = redirectBase
          ? buildUrlWithBikeParams(redirectBase, bike.bike_make, bike.bike_model, bike.bike_year)
          : '';

        if (viewAll) viewAll.href = redirectUrl || fallbackUrl || '/collections/all';
      } catch {
        legacy.hidden = true;
        if (bikeNameEl) bikeNameEl.textContent = '';
        if (viewAll) viewAll.href = fallbackUrl || '/collections/all';
      }

      return; // legacy handled
    }

    // 2) NEW shared section on homepage: your Liquid uses [data-dr-pdp-fitment]
    const isHome =
      (window.location.pathname || '') === '/' ||
      document.body.classList.contains('template-index') ||
      document.body.classList.contains('template-index');

    if (!isHome) return;

    const el = document.querySelector('[data-dr-pdp-fitment]');
    if (!el) return;
    el.style.display = 'none';

    const titleEl = el.querySelector('[data-dr-pdp-fitment-title]');
    const subEl   = el.querySelector('[data-dr-pdp-fitment-sub]');
    const imgEl   = el.querySelector('[data-dr-pdp-fitment-brand-img]');
    const moreBtn = el.querySelector('[data-dr-pdp-fitment-more]');

    // Helpers: only use text if the attribute exists, otherwise output nothing.
    const hasAttr = (name) => el.hasAttribute(name);
    const getText = (attrName, datasetKey) => (hasAttr(attrName) ? (el.dataset[datasetKey] || '') : '');

    const collectionPath = el.getAttribute('data-collection-path') || '/collections/all';
    const productType = (el.getAttribute('data-product-type') || 'products').toLowerCase();

    try {
      const { hasBike, bike } = await getActiveBike();

      // No bike set: hide the entire homepage section
      if (!hasBike) {
        el.style.display = 'none';

        // Optional: clear any previous content just in case
        if (titleEl) titleEl.textContent = '';
        if (subEl) subEl.textContent = '';
        if (moreBtn) moreBtn.style.display = 'none';
        if (imgEl) imgEl.style.display = 'none';

        return;
      }

      // Bike set: ensure the section is visible
      el.style.display = '';

      // Bike set: update subtitle + logo + show CTA
      const foundPrefix = getText('data-text-found-prefix', 'textFoundPrefix');

      // Use the same format as everywhere else: Make Model Year (no trim)
      const bikeName = (formatBikeName(bike) || '').trim();

      const parts = [];
      if (foundPrefix) parts.push(foundPrefix);
      if (bikeName) parts.push(bikeName);

      // Title choice for homepage when bike is set:
      // Prefer "ok" title if it exists, else fall back to default title, else nothing.
      const titleOk = getText('data-text-title-ok', 'textTitleOk');
      const titleDefault = getText('data-text-title-default', 'textTitleDefault');
      if (titleEl) titleEl.textContent = titleOk || titleDefault || '';

      if (subEl) subEl.textContent = parts.join(' ').trim();

      // Logo (optional)
      const logos = window.DR_BRAND_LOGOS || {};
      const key = normalizeMakeKey(bike.bike_make);
      const logoUrl = logos[key];

      if (imgEl && logoUrl) {
        imgEl.src = logoUrl;
        imgEl.alt = bike.bike_make || '';
        imgEl.style.display = '';
      } else if (imgEl) {
        imgEl.style.display = 'none';
      }

      // CTA: View compatible products for the active bike
      const redirectBase = resolveBikeRedirectPath() || collectionPath;
      const redirectUrl = buildUrlWithBikeParams(redirectBase, bike.bike_make, bike.bike_model, bike.bike_year);

      if (moreBtn) {
        moreBtn.href = redirectUrl || collectionPath || '/collections/all';
        moreBtn.textContent = `View compatible ${productType}`;
        moreBtn.style.display = '';
      }
    } catch {
      // Fail safe: revert to defaults
      if (titleEl) titleEl.textContent = getText('data-text-title-default', 'textTitleDefault');
      if (subEl)   subEl.textContent   = getText('data-text-sub-default',   'textSubDefault');
      if (moreBtn) moreBtn.style.display = 'none';
      if (imgEl) imgEl.style.display = 'none';
    }
  }
   
  // -----------------------------
  // Product Page Search By Bike
  // -----------------------------
    function normalizeUvc(v) {
      return String(v || '').trim().toUpperCase();
    }

    function coerceToArray(val) {
      if (Array.isArray(val)) return val;
      if (val == null) return [];
      if (typeof val === 'string') {
        const s = val.trim();
        if (!s) return [];
        // try JSON first
        try {
          const j = JSON.parse(s);
          if (Array.isArray(j)) return j;
        } catch {}
        // fallback: comma-separated
        return s.split(',').map(x => x.trim()).filter(Boolean);
      }
      return [];
    }

    function normKey(s) {
      return String(s || '')
        .trim()
        .toLowerCase()
        .replace(/\u00a0/g, ' ')
        .replace(/\s+/g, ' ')
        .replace(/[’]/g, "'");
    }

    function buildActiveBikeKey(bike) {
      if (!bike) return null;
      const make = normKey(bike.bike_make);
      const model = normKey(bike.bike_model);
      const yearNum = parseInt(String(bike.bike_year || '').trim(), 10);
      if (!make || !model || !Number.isFinite(yearNum)) return null;
      return { make, model, year: yearNum };
    }

    function parseCompatibleBikesFromValue(raw) {
      const list = coerceToArray(raw);
      return list
        .map((entry) => {
          const parts = String(entry || '').split('|').map((part) => part.trim());
          if (parts.length < 3) return null;

          const make = normKey(parts[0]);
          const model = normKey(parts[1]);
          const yearPart = parts.slice(2).join('|').trim();
          if (!make || !model || !yearPart) return null;

          let start = null;
          let end = null;

          const range = yearPart.match(/^(\d{4})\s*-\s*(\d{4})$/);
          if (range) {
            start = parseInt(range[1], 10);
            end = parseInt(range[2], 10);
          } else {
            const single = yearPart.match(/^(\d{4})$/);
            if (single) {
              start = parseInt(single[1], 10);
              end = start;
            }
          }

          if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
          if (end < start) [start, end] = [end, start];

          return { make, model, start, end };
        })
        .filter(Boolean);
    }

    function matchesCompatibleBikes(active, compatibleList) {
      if (!active || !Number.isFinite(active.year)) return false;
      if (!Array.isArray(compatibleList) || !compatibleList.length) return false;

      return compatibleList.some((entry) => {
        if (!entry || typeof entry !== 'object') return false;
        if (entry.make !== active.make || entry.model !== active.model) return false;
        if (!Number.isFinite(entry.start) || !Number.isFinite(entry.end)) return false;
        return active.year >= entry.start && active.year <= entry.end;
      });
    }

    function getCompatibleBikesForElement(el, key = 'compatibleBikes') {
      if (!el) return [];

      let datasetKey = key;
      if (typeof key === 'string' && key.startsWith('data-')) {
        datasetKey = key
          .slice(5)
          .replace(/-([a-z])/g, (_, c) => c.toUpperCase());
      }

      const raw = el.dataset?.[datasetKey] || '';

      let perEl = COMPATIBLE_BIKES_CACHE.get(el);
      if (!perEl) {
        perEl = new Map();
        COMPATIBLE_BIKES_CACHE.set(el, perEl);
      }

      const cached = perEl.get(datasetKey);
      if (cached && cached.raw === raw) return cached.parsed;

      const parsed = parseCompatibleBikesFromValue(raw);
      perEl.set(datasetKey, { raw, parsed });
      return parsed;
    }
    
    async function initPdpFitmentConfirm() {
      const el = document.querySelector('[data-dr-pdp-fitment]');
      if (!el) return;
      if (!(window.location.pathname || '').startsWith('/products/')) return;

      const titleEl = el.querySelector('[data-dr-pdp-fitment-title]');
      const subEl   = el.querySelector('[data-dr-pdp-fitment-sub]');
      const imgEl   = el.querySelector('[data-dr-pdp-fitment-brand-img]');
      const moreBtn = el.querySelector('[data-dr-pdp-fitment-more]');

      const bikeSpecific = String(el.getAttribute('data-bike-specific')) === 'true';

      // If not bike-specific, output nothing (and ideally the Liquid snippet won’t render at all)
      if (!bikeSpecific) {
        el.remove();
        return;
      }

      const collectionPath = el.getAttribute('data-collection-path') || '/collections/all';
      const productType = (el.getAttribute('data-product-type') || 'products').toLowerCase();

      // Helpers: only use text when the attribute exists; otherwise output nothing.
      const hasAttr = (name) => el.hasAttribute(name);
      const getText = (attrName, datasetKey) => (hasAttr(attrName) ? (el.dataset[datasetKey] || '') : '');

      const compatibleBikes = getCompatibleBikesForElement(el, 'compatibleBikes');

      try {
        const { hasBike, bike } = await getActiveBike();

        // No active bike yet → prompt to check fit
        if (!hasBike) {
          el.classList.remove('is-ok', 'is-bad');

          // If the setting is blank, Liquid should omit the attribute, and this will output ''.
          if (titleEl) titleEl.textContent = getText('data-text-title-default', 'textTitleDefault');
          if (subEl)   subEl.textContent   = getText('data-text-sub-default',   'textSubDefault');

          if (moreBtn) moreBtn.style.display = 'none';
          if (imgEl) imgEl.style.display = 'none';
          return;
        }

        // Show make logo (optional)
        const logos = window.DR_BRAND_LOGOS || {};
        const key = normalizeMakeKey(bike.bike_make);
        const logoUrl = logos[key];

        if (imgEl && logoUrl) {
          imgEl.src = logoUrl;
          imgEl.alt = bike.bike_make || '';
          imgEl.style.display = '';
        } else if (imgEl) {
          imgEl.style.display = 'none';
        }

        const activeBike = buildActiveBikeKey(bike);
        const isCompatible = matchesCompatibleBikes(activeBike, compatibleBikes);

        // These are used in both ok/bad states. If blank, they become ''.
        const foundPrefix    = getText('data-text-found-prefix',     'textFoundPrefix');
        const platformSuffix = getText('data-text-platform-suffix',  'textPlatformSuffix');

        // Build subtitle without awkward double spaces when prefix/suffix are blank
        const buildSub = () => {
          const parts = [];
          if (foundPrefix) parts.push(foundPrefix);
          const bikeName = formatBikeName(bike);
          if (bikeName) parts.push(bikeName);
          if (platformSuffix) parts.push(platformSuffix);
          return parts.join(' ').trim();
        };

        if (isCompatible) {
          el.classList.add('is-ok');
          el.classList.remove('is-bad');

          if (titleEl) titleEl.textContent = getText('data-text-title-ok', 'textTitleOk');
          if (subEl)   subEl.textContent   = buildSub();

          if (moreBtn) {
            moreBtn.href = buildUrlWithBikeParams(collectionPath, bike.bike_make, bike.bike_model, bike.bike_year);
            moreBtn.textContent = `View compatible ${productType}`;
            moreBtn.style.display = '';
          }
          return;
        }

        // Not compatible
        el.classList.add('is-bad');
        el.classList.remove('is-ok');

        if (titleEl) titleEl.textContent = getText('data-text-title-bad', 'textTitleBad');
        if (subEl)   subEl.textContent   = buildSub();

        if (moreBtn) {
          moreBtn.href = buildUrlWithBikeParams(collectionPath, bike.bike_make, bike.bike_model, bike.bike_year);
          moreBtn.textContent = `View compatible ${productType}`;
          moreBtn.style.display = '';
        }
      } catch {
        // Fail safe
        el.classList.remove('is-ok', 'is-bad');

        if (titleEl) titleEl.textContent = getText('data-text-title-default', 'textTitleDefault');
        if (subEl)   subEl.textContent   = getText('data-text-sub-default',   'textSubDefault');

        if (moreBtn) moreBtn.style.display = 'none';
        if (imgEl) imgEl.style.display = 'none';
      }
    }




    


  async function initPdpFitBadge() {
    const badges = qsa('[data-dr-fit-badge], [data-dr-assured-fit-badge]');
    if (!badges.length) return;

    const hide = (el) => {
      el.setAttribute('hidden', 'hidden');
      el.style.display = 'none';
    };

    const show = (el) => {
      el.removeAttribute('hidden');
      el.style.display = '';
    };

    try {
      const { hasBike, bike } = await getActiveBike();
      const activeBike = hasBike ? buildActiveBikeKey(bike) : null;

      if (window.DR_DEBUG_FIT) {
        console.log('[DR Fit] active bike key', activeBike);
      }

      // No bike set => hide everything
      if (!hasBike || !activeBike) {
        badges.forEach(hide);
        return;
      }

      badges.forEach((badge) => {
        const bikeSpecific = String(badge.dataset.drBikeSpecific) === 'true';
        const compatibleRaw = badge.dataset?.compatibleBikes || '';
        if (!bikeSpecific) {
          if (window.DR_DEBUG_FIT) {
            console.log('[DR Fit] badge check', {
              bikeSpecific,
              rawCompatibleLength: compatibleRaw.length,
              match: false
            });
          }
          hide(badge);
          return;
        }

        // IMPORTANT: dataset key is "compatibleBikes" for data-compatible-bikes=""
        const compatibleBikes = getCompatibleBikesForElement(badge, 'compatibleBikes');

        // If metafield missing/empty => MUST be hidden
        if (!Array.isArray(compatibleBikes) || compatibleBikes.length === 0) {
          if (window.DR_DEBUG_FIT) {
            console.log('[DR Fit] badge check', {
              bikeSpecific,
              rawCompatibleLength: compatibleRaw.length,
              match: false
            });
          }
          hide(badge);
          return;
        }

        const isCompatible = matchesCompatibleBikes(activeBike, compatibleBikes);
        if (window.DR_DEBUG_FIT) {
          console.log('[DR Fit] badge check', {
            bikeSpecific,
            rawCompatibleLength: compatibleRaw.length,
            match: isCompatible
          });
        }
        if (isCompatible) show(badge);
        else hide(badge);
      });
    } catch {
      badges.forEach(hide);
    }
  }

  window.DRFitBadgeRefresh = window.DRFitBadgeRefresh || function () {
    initPdpFitBadge().catch(() => {});
  };

  // -----------------------------
  // Bike Finder (supports multiple instances)
  // -----------------------------
  function openNativeSelect(selectEl) {
    if (!selectEl) return;
    selectEl.focus();

    if (typeof selectEl.showPicker === 'function') {
      try { selectEl.showPicker(); return; } catch {}
    }

    try { selectEl.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true })); } catch {}
    try { selectEl.click(); } catch {}
  }

  async function initOneBikeFinder(root) {
    if (!root) return;

    const mode = root.getAttribute('data-dr-bike-finder-mode') || 'page'; // page | modal
    const isModal = mode === 'modal';

    const tabVrm = qs('[data-tab="vrm"]', root);
    const tabManual = qs('[data-tab="manual"]', root);
    const panelVrm = qs('[data-panel="vrm"]', root);
    const panelManual = qs('[data-panel="manual"]', root);

    const modePanelsWrap = qs('.drbf__modePanels', root);

    function measurePanelHeight(panel) {
      if (!panel) return 0;
      const prev = {
        hiddenAttr: panel.hasAttribute('hidden'),
        activeClass: panel.classList.contains('is-active'),
        display: panel.style.display,
        position: panel.style.position,
        visibility: panel.style.visibility,
        pointerEvents: panel.style.pointerEvents,
        inset: panel.style.inset
      };

      panel.removeAttribute('hidden');
      panel.classList.add('is-active');
      panel.style.display = 'block';
      panel.style.position = 'absolute';
      panel.style.visibility = 'hidden';
      panel.style.pointerEvents = 'none';
      panel.style.inset = '0';

      const height = panel.offsetHeight;

      panel.style.display = prev.display;
      panel.style.position = prev.position;
      panel.style.visibility = prev.visibility;
      panel.style.pointerEvents = prev.pointerEvents;
      panel.style.inset = prev.inset;
      if (!prev.activeClass) panel.classList.remove('is-active');
      if (prev.hiddenAttr) panel.setAttribute('hidden', '');

      return height;
    }

    function syncModePanelsMinHeight() {
      if (!modePanelsWrap) return;
      const maxHeight = Math.max(measurePanelHeight(panelVrm), measurePanelHeight(panelManual));
      if (maxHeight > 0) modePanelsWrap.style.minHeight = `${maxHeight}px`;
    }

    const syncModePanelsMinHeightDebounced = debounce(syncModePanelsMinHeight, 120);

    // Manual
    const makeSel = qs('[data-make-select]', root);
    const modelSel = qs('[data-model-select]', root);
    const yearSel = qs('[data-year-select]', root);
    const manualBtn = qs('[data-manual-submit]', root);
    const manualMsg = qs('[data-manual-msg]', root);
    const manualHelper = qs('[data-manual-helper]', root);

    // VRM
    const vrmInput = qs('[data-vrm-input]', root);
    const vrmBtn = qs('[data-vrm-submit]', root);
    const vrmMsg = qs('[data-vrm-msg]', root);

    const plateWrap = vrmInput?.closest('.drbf__plate');
    const manualRow = makeSel?.closest('.drbf__manualRow') || qs('.drbf__manualRow', root);

    let msgIdSeq = 0;
    function ensureElementId(el, prefix) {
      if (!el) return '';
      if (!el.id) {
        msgIdSeq += 1;
        el.id = `${prefix}-${msgIdSeq}`;
      }
      return el.id;
    }

    function setErrorState({ control, wrapper, msgEl, isError }) {
      if (wrapper) wrapper.classList.toggle('is-error', !!isError);
      if (control) {
        if (isError) control.setAttribute('aria-invalid', 'true');
        else control.removeAttribute('aria-invalid');
      }
      if (control && msgEl) {
        const msgId = ensureElementId(msgEl, 'drbf-msg');
        if (isError) control.setAttribute('aria-describedby', msgId);
        else if (control.getAttribute('aria-describedby') === msgId) control.removeAttribute('aria-describedby');
      }
    }

    function clearManualErrors() {
      setErrorState({ control: makeSel, wrapper: manualRow, msgEl: manualMsg, isError: false });
      setErrorState({ control: modelSel, wrapper: manualRow, msgEl: manualMsg, isError: false });
      setErrorState({ control: yearSel, wrapper: manualRow, msgEl: manualMsg, isError: false });
    }

    let helperStep = '';
    function updateManualHelper() {
      if (!manualHelper) return;
      let nextStep = 'make';
      if (makeSel?.value && !modelSel?.value) nextStep = 'model';
      else if (makeSel?.value && modelSel?.value && !yearSel?.value) nextStep = 'year';
      else if (makeSel?.value && modelSel?.value && yearSel?.value) nextStep = 'ready';

      if (nextStep === helperStep) return;
      helperStep = nextStep;

      if (nextStep === 'make') setText(manualHelper, root.dataset.textManualHelperMake || 'Select a make to unlock model');
      else if (nextStep === 'model') setText(manualHelper, root.dataset.textManualHelperModel || 'Select a model to unlock year');
      else setText(manualHelper, root.dataset.textManualHelperYear || 'Select a year to enable Find bike');
      syncModePanelsMinHeightDebounced();
    }

    function fillSelect(select, values, placeholder) {
      if (!select) return;
      const opts = [`<option value="">${placeholder || 'Select'}</option>`]
        .concat((values || []).map(v => `<option value="${String(v).replace(/"/g, '&quot;')}">${v}</option>`));
      select.innerHTML = opts.join('');
    }

    const makeComboWrap = makeSel?.closest('[data-dr-combo]') || makeSel?.parentElement;
    const modelComboWrap = modelSel?.closest('[data-dr-combo]') || modelSel?.parentElement;
    const yearComboWrap = yearSel?.closest('[data-dr-combo]') || yearSel?.parentElement;
    let makeCombo = null;
    let modelCombo = null;
    let yearCombo = null;
    const comboInstances = [];

    function closeAllCombosExcept(exceptWrapEl) {
      comboInstances.forEach((combo) => {
        if (!combo || (exceptWrapEl && combo.wrapEl === exceptWrapEl)) return;
        combo.close();
      });
      qsa('.drbf__comboWrap.is-open, .drbf__comboboxWrap.is-open', root).forEach((wrap) => {
        if (exceptWrapEl && wrap === exceptWrapEl) return;
        wrap.classList.remove('is-open');
        const trigger = qs('.drbf__comboTrigger[aria-expanded="true"]', wrap);
        const pop = qs('.drbf__comboPop', wrap);
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        if (pop) pop.hidden = true;
      });
    }

    function createCombo({ selectEl, wrapEl, label, placeholder, getSource, getSourceHtml }) {
      if (!selectEl || !wrapEl) return null;
      const uid = selectEl.id || `drbf-${label.toLowerCase()}-${Math.random().toString(36).slice(2, 9)}`;
      selectEl.id = uid;
      const listId = `${uid}-listbox`;
      const popId = `${uid}-pop`;
      const triggerId = `${uid}-trigger`;

      const uiRoot = qs('[data-dr-combo-ui]', wrapEl) || wrapEl;
      uiRoot.innerHTML = '';

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.id = triggerId;
      trigger.className = 'drbf__comboTrigger';
      trigger.setAttribute('role', 'combobox');
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', listId);
      trigger.setAttribute('aria-label', selectEl.getAttribute('aria-label') || label);
      trigger.innerHTML = `<span class="drbf__comboText">${placeholder}</span><span class="drbf__comboCaret" aria-hidden="true"></span>`;

      const pop = document.createElement('div');
      pop.className = 'drbf__comboPop';
      pop.id = popId;
      pop.hidden = true;
      pop.innerHTML = `<input type="text" class="drbf__comboSearch" aria-label="Search ${label.toLowerCase()}" placeholder="Search ${label.toLowerCase()}" autocomplete="off"><ul class="drbf__comboList" id="${listId}" role="listbox" aria-labelledby="${triggerId}"></ul><div class="drbf__comboHint" hidden></div>`;

      uiRoot.appendChild(trigger);
      uiRoot.appendChild(pop);
      selectEl.classList.add('drbf__select--enhanced');

      const search = qs('.drbf__comboSearch', pop);
      const list = qs('.drbf__comboList', pop);
      const hint = qs('.drbf__comboHint', pop);
      let source = [];
      let filtered = [];
      let active = -1;
      let sourceHtml = '';
      let hovered = -1;
      const comboType = selectEl === makeSel ? 'make' : (selectEl === modelSel ? 'model' : (selectEl === yearSel ? 'year' : 'other'));

      const render = () => {
        const limited = filtered.slice(0, MODEL_RENDER_LIMIT);
        if (!limited.length) {
          list.innerHTML = '<li class="drbf__comboEmpty" aria-disabled="true">No results found</li>';
        } else if (!search.value && active === -1 && sourceHtml) {
          list.innerHTML = sourceHtml;
        } else {
          list.innerHTML = limited.map((row, idx) => `<li role="option" id="${listId}-${idx}" class="drbf__comboOption${idx === active ? ' is-active' : ''}" aria-selected="${idx === active}" data-idx="${idx}" data-value="${String(row.value).replace(/"/g, '&quot;')}">${row.label}</li>`).join('');
        }
        if (filtered.length > MODEL_RENDER_LIMIT) {
          hint.hidden = false;
          hint.textContent = 'Keep typing to narrow results';
        } else {
          hint.hidden = true;
        }
        if (active >= 0 && limited[active]) trigger.setAttribute('aria-activedescendant', `${listId}-${active}`);
        else trigger.removeAttribute('aria-activedescendant');
      };

      const applyFilter = (term = '') => {
        const q = normalizeQuery(term);
        hovered = -1;
        if (!q) filtered = source;
        else {
          const exact = [];
          const starts = [];
          const contains = [];
          source.forEach(item => {
            if (item.norm === q) exact.push(item);
            else if (item.norm.startsWith(q)) starts.push(item);
            else if (item.norm.includes(q)) contains.push(item);
          });
          const stage1 = exact.concat(starts, contains);
          filtered = stage1;

          const weakStage1 = stage1.length < 8;
          const shouldTokenScore = weakStage1 && comboType === 'model';
          if (shouldTokenScore) {
            const qTokens = buildTokens(term);
            if (qTokens.size) {
              let tokenCandidates = source;
              if (source.length > 2000) {
                const merged = [];
                const seen = new Set();
                const addCandidate = (item) => {
                  const key = String(item.value);
                  if (seen.has(key)) return;
                  seen.add(key);
                  merged.push(item);
                };
                stage1.forEach(addCandidate);
                source.slice(0, 2000).forEach(addCandidate);
                tokenCandidates = merged;
              }

              filtered = tokenCandidates
                .map((item) => {
                  const itemTokens = item.tokens || new Set();
                  let hits = 0;
                  qTokens.forEach((token) => {
                    if (itemTokens.has(token)) hits += 1;
                  });
                  return { item, score: scoreByTokens(qTokens, itemTokens), hits };
                })
                .filter((entry) => entry.score > -1e8)
                .filter((entry) => comboType !== 'model' || !q || entry.hits >= 1)
                .sort((a, b) => {
                  if (b.score !== a.score) return b.score - a.score;
                  return a.item.label.localeCompare(b.item.label);
                })
                .map((entry) => entry.item);

              if (!filtered.length) filtered = stage1;
            }
          }
        }
        active = -1;
        render();
      };

      const debouncedFilter = debounce(() => applyFilter(search.value), 50);

      const close = () => {
        pop.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        wrapEl.classList.remove('is-open');
      };

      const open = (focusSearch = true) => {
        if (trigger.disabled) return;
        closeAllCombosExcept(wrapEl);
        pop.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        wrapEl.classList.add('is-open');
        search.value = '';
        applyFilter('');
        if (focusSearch) window.setTimeout(() => search.focus(), 0);
      };

      const selectValue = (value, closeAfter = true) => {
        if (!value) return;
        selectEl.value = value;
        trigger.querySelector('.drbf__comboText').textContent = value;
        selectEl.dispatchEvent(new Event('change', { bubbles: true }));
        if (closeAfter) close();
      };

      trigger.addEventListener('click', () => (pop.hidden ? open() : close()));
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          open();
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          close();
        }
      });

      search.addEventListener('input', debouncedFilter);
      search.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          close();
          trigger.focus();
          return;
        }
        if (!filtered.length) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); active = Math.min(active + 1, Math.min(filtered.length, MODEL_RENDER_LIMIT) - 1); render(); }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          if (active < 0) active = 0;
          else active = Math.max(active - 1, 0);
          render();
        }
        if (e.key === 'Enter') { e.preventDefault(); if (active >= 0 && filtered[active]) selectValue(filtered[active].value); }
      });

      list.addEventListener('mousemove', (e) => {
        const opt = e.target.closest('.drbf__comboOption');
        if (!opt) return;
        const idx = Number(opt.getAttribute('data-idx'));
        if (idx < 0 || idx === hovered) return;
        hovered = idx;
        list.querySelectorAll('.drbf__comboOption.is-hovered').forEach((el) => el.classList.remove('is-hovered'));
        opt.classList.add('is-hovered');
      });

      list.addEventListener('click', (e) => {
        const opt = e.target.closest('.drbf__comboOption');
        if (!opt) return;
        selectValue(opt.getAttribute('data-value'));
      });

      document.addEventListener('click', (e) => {
        if (!wrapEl.contains(e.target)) close();
      });

      return {
        wrapEl,
        setSource(ctx, values = []) {
          source = getSource(ctx, values);
          sourceHtml = typeof getSourceHtml === 'function' ? getSourceHtml(ctx, values) : '';
          trigger.querySelector('.drbf__comboText').textContent = selectEl.value || placeholder;
          applyFilter('');
        },
        setEnabled(enabled) {
          trigger.disabled = !enabled;
          wrapEl.classList.toggle('is-disabled', !enabled);
          if (!enabled) {
            close();
            trigger.querySelector('.drbf__comboText').textContent = placeholder;
          }
        },
        open,
        close,
        focusControl() {
          if (!pop.hidden) search.focus();
          else trigger.focus();
        },
        syncToSelect() {
          trigger.querySelector('.drbf__comboText').textContent = selectEl.value || placeholder;
        }
      };
    }
    function setManualState({ modelEnabled, yearEnabled, canSearch }) {
      if (makeCombo) makeCombo.setEnabled(true);
      if (modelSel) modelSel.disabled = !modelEnabled;
      if (yearSel) yearSel.disabled = !yearEnabled;
      if (modelCombo) modelCombo.setEnabled(!!modelEnabled);
      if (yearCombo) yearCombo.setEnabled(!!yearEnabled);
      if (manualBtn) manualBtn.disabled = !canSearch;
      updateManualHelper();
    }

    async function loadMakes() {
      const values = await fetchMakesCached();
      fillSelect(makeSel, values, 'Make');
      makeCombo?.setSource('', values);
      makeCombo?.syncToSelect();
      fillSelect(modelSel, [], 'Model');
      modelCombo?.setSource('', []);
      modelCombo?.syncToSelect();
      fillSelect(yearSel, [], 'Year');
      yearCombo?.setSource('', []);
      yearCombo?.syncToSelect();
      setManualState({ modelEnabled: false, yearEnabled: false, canSearch: false });
    }

    async function loadModels(make) {
      const values = await fetchModelsCached(make);
      fillSelect(modelSel, values, 'Model');
      modelCombo?.setSource(make, values);
    }

    async function loadYears(make, model) {
      const values = await fetchYearsCached(make, model);
      fillSelect(yearSel, values, 'Year');
      yearCombo?.setSource(`${make}|||${model}`, values);
    }

    function setSelectToMatch(select, target) {
      if (!select || !target) return false;
      const t = String(target).trim().toLowerCase();
      const opt =
        Array.from(select.options).find(o => String(o.value).trim().toLowerCase() === t) ||
        Array.from(select.options).find(o => String(o.textContent).trim().toLowerCase() === t);
      if (opt) {
        select.value = opt.value;
        return true;
      }
      return false;
    }

    let prefillInFlight = false;
    async function prefillManualFromActiveBike() {
      if (prefillInFlight) return;
      prefillInFlight = true;
      try {
        const { hasBike, bike } = await getActiveBike();
        if (!hasBike) return;
        if (!makeSel || makeSel.options.length < 2) return;

        const makeOk = setSelectToMatch(makeSel, bike.bike_make);
        if (!makeOk) return;
        makeCombo?.syncToSelect();

        setManualState({ modelEnabled: false, yearEnabled: false, canSearch: false });

        if (modelSel) { modelSel.disabled = true; fillSelect(modelSel, [], '…'); }
        await loadModels(makeSel.value);

        const modelOk = setSelectToMatch(modelSel, bike.bike_model);
        if (!modelOk) return;
        modelCombo?.syncToSelect();

        if (yearSel) { yearSel.disabled = true; fillSelect(yearSel, [], '…'); }
        await loadYears(makeSel.value, modelSel.value);

        const yearOk = setSelectToMatch(yearSel, bike.bike_year);
        if (!yearOk) return;
        yearCombo?.syncToSelect();

        setManualState({ modelEnabled: true, yearEnabled: true, canSearch: true });
      } finally {
        prefillInFlight = false;
      }
    }

    function canAutoOpenNext(combo, selectEl) {
      if (!combo || !selectEl) return false;
      if (selectEl.disabled) return false;
      const wrapEl = combo.wrapEl;
      const trigger = wrapEl ? qs('.drbf__comboTrigger', wrapEl) : null;
      if (trigger?.disabled) return false;
      if (wrapEl?.classList.contains('is-disabled')) return false;
      if (panelManual?.getAttribute('aria-hidden') === 'true' || panelManual?.hidden) return false;
      const modalRoot = root.closest('[data-dr-garage-modal]');
      if (modalRoot?.getAttribute('aria-hidden') === 'true') return false;
      return Array.from(selectEl.options || []).some((opt) => String(opt.value || '').trim());
    }

    function queueOpenNext(nextKey) {
      const next = nextKey === 'model'
        ? { combo: modelCombo, select: modelSel }
        : nextKey === 'year'
          ? { combo: yearCombo, select: yearSel }
          : null;
      if (!next) return;
      requestAnimationFrame(() => {
        if (!canAutoOpenNext(next.combo, next.select)) return;
        next.combo.open();
      });
    }

    function setTab(which) {
      closeAllCombosExcept(null);
      const isVrm = which === 'vrm';
      tabVrm?.classList.toggle('is-active', isVrm);
      tabManual?.classList.toggle('is-active', !isVrm);
      tabVrm?.setAttribute('aria-selected', String(isVrm));
      tabManual?.setAttribute('aria-selected', String(!isVrm));
      tabVrm?.setAttribute('tabindex', isVrm ? '0' : '-1');
      tabManual?.setAttribute('tabindex', isVrm ? '-1' : '0');
      panelVrm?.classList.toggle('is-active', isVrm);
      panelManual?.classList.toggle('is-active', !isVrm);
      panelVrm?.toggleAttribute('hidden', !isVrm);
      panelManual?.toggleAttribute('hidden', isVrm);
      panelVrm?.setAttribute('aria-hidden', String(!isVrm));
      panelManual?.setAttribute('aria-hidden', String(isVrm));

      if (which === 'manual') {
        setTimeout(() => { prefillManualFromActiveBike().catch(() => {}); }, 0);
      }

      syncModePanelsMinHeightDebounced();

      setTimeout(() => {
        if (isVrm) {
          vrmInput?.focus();
          return;
        }
        if (makeCombo) {
          makeCombo.focusControl();
          return;
        }
        makeSel?.focus();
      }, 0);
    }

    tabVrm?.addEventListener('click', () => setTab('vrm'));
    tabManual?.addEventListener('click', () => setTab('manual'));

    if (!bikeDataCache.makeIndex && bikeDataCache.makes?.length) {
      bikeDataCache.makeIndex = buildOptionIndex(bikeDataCache.makes);
    }

    makeCombo = createCombo({
      selectEl: makeSel,
      wrapEl: makeComboWrap,
      label: 'Make',
      placeholder: 'Make',
      getSource: (_, values) => {
        if (!bikeDataCache.makeIndex || bikeDataCache.makeIndex.length !== values.length) bikeDataCache.makeIndex = buildOptionIndex(values);
        return bikeDataCache.makeIndex;
      },
      getSourceHtml: () => bikeDataCache.makeOptionsHtml
    });
    modelCombo = createCombo({
      selectEl: modelSel,
      wrapEl: modelComboWrap,
      label: 'Model',
      placeholder: 'Model',
      getSource: (make, values) => modelIndexForMake(make, values),
      getSourceHtml: (make) => bikeDataCache.modelOptionsHtmlByMake.get(String(make || '')) || ''
    });
    yearCombo = createCombo({
      selectEl: yearSel,
      wrapEl: yearComboWrap,
      label: 'Year',
      placeholder: 'Year',
      getSource: (ctx, values) => {
        const [make, model] = String(ctx || '').split('|||');
        return yearIndexForMakeModel(make, model, values);
      },
      getSourceHtml: (ctx) => bikeDataCache.yearOptionsHtmlByKey.get(String(ctx || '')) || ''
    });


    [makeCombo, modelCombo, yearCombo].forEach((combo) => {
      if (combo) comboInstances.push(combo);
    });

    window.addEventListener('resize', syncModePanelsMinHeightDebounced);
    syncModePanelsMinHeight();

    // VRM UX
    vrmInput?.addEventListener('input', () => {
      const start = vrmInput.selectionStart;
      const next = sanitizeVrmTyping(vrmInput.value);
      if (next !== vrmInput.value) {
        vrmInput.value = next;
        if (typeof start === 'number') {
          const pos = Math.min(start, next.length);
          vrmInput.setSelectionRange(pos, pos);
        }
      }
      if (vrmInput.getAttribute('aria-invalid') === 'true') showVrmMessage('', false);
    });

    vrmInput?.addEventListener('blur', () => {
      vrmInput.value = formatVrmDisplay(vrmInput.value);
    });

    vrmInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        vrmBtn?.click();
      }
    });

    function setMessageState(msgEl, isError) {
      if (!msgEl || !isModal) return;
      if (isError) {
        msgEl.setAttribute('aria-live', 'assertive');
        msgEl.setAttribute('role', 'alert');
      } else {
        msgEl.setAttribute('aria-live', 'polite');
        msgEl.removeAttribute('role');
      }
    }

    function showVrmMessage(msg, isError = false) {
      if (!vrmMsg) return;
      setMessageState(vrmMsg, isError);
      vrmMsg.textContent = msg || '';
      setErrorState({ control: vrmInput, wrapper: plateWrap, msgEl: vrmMsg, isError: !!isError });
    }

    function showManualMessage(msg, isError = false, controls = [makeSel, modelSel, yearSel]) {
      if (!manualMsg) return;
      setMessageState(manualMsg, isError);
      manualMsg.textContent = msg || '';
      clearManualErrors();
      if (isError) {
        controls.filter(Boolean).forEach((control) => {
          setErrorState({ control, wrapper: manualRow, msgEl: manualMsg, isError: true });
        });
      }
    }

    async function onBikeResolved(bike) {
      if (!isModal) {
        await applyBikeAndGo(bike);
        return;
      }

      // Modal mode: apply then navigate so collection updates
      await applyBike(bike);

      if ((window.location.pathname || '').startsWith('/products/')) {
        if (isCurrentPdpBikeSpecific()) {
          window.location.reload();
        } else {
          const url = buildUrlWithBikeParams('/collections/all', bike.bike_make, bike.bike_model, bike.bike_year);
          window.location.assign(url);
        }
        return;
      }

      // ✅ Otherwise behave as before (collections filtered)
      const basePath = defaultRedirectCollectionPath();
      const url = buildUrlWithBikeParams(basePath, bike.bike_make, bike.bike_model, bike.bike_year);
      window.location.assign(url);

    }


    vrmBtn?.addEventListener('click', async () => {
      const raw = vrmInput?.value || '';
      const norm = normalizeVrm(raw);

      showVrmMessage('');
      if (!norm) { showVrmMessage(root.dataset.textMsgEnterRegistration || 'Enter registration.', true); return; }

      try {
        setLoading(vrmBtn, true);
        if (vrmInput) vrmInput.disabled = true;

        const res = await apiGet('vrm-lookup', { vrm: norm });

        if (res.status !== 'ok' || !res.bike) {
          if (vrmInput) vrmInput.value = sanitizeVrmTyping(raw);
          showVrmMessage(root.dataset.textMsgNoRegMatch || 'No match. Use Make / Model / Year.', true);
          setTab('manual');
          return;
        }

        await onBikeResolved(res.bike);
      } catch {
        showVrmMessage(root.dataset.textMsgRegError || 'Could not verify registration. Use Make / Model / Year.', true);
        setTab('manual');
      } finally {
        setLoading(vrmBtn, false);
        if (vrmInput) vrmInput.disabled = false;
      }
    });

    // Manual UX (auto-advance)
    makeSel?.addEventListener('change', async () => {
      showManualMessage('');
      makeCombo?.syncToSelect();
      fillSelect(modelSel, [], 'Model');
      modelCombo?.setSource('', []);
      modelCombo?.syncToSelect();
      fillSelect(yearSel, [], 'Year');
      yearCombo?.setSource('', []);
      yearCombo?.syncToSelect();
      setManualState({ modelEnabled: false, yearEnabled: false, canSearch: false });

      const make = makeSel.value;
      if (!make) return;

      try {
        if (modelSel) { modelSel.disabled = true; fillSelect(modelSel, [], '…'); }
        await loadModels(make);
        setManualState({ modelEnabled: true, yearEnabled: false, canSearch: false });
        queueOpenNext('model');
      } catch {
        showManualMessage(root.dataset.textMsgLoadModelsError || 'Could not load models.', true, [makeSel]);
      }
    });

    modelSel?.addEventListener('change', async () => {
      showManualMessage('');
      modelCombo?.syncToSelect();
      fillSelect(yearSel, [], 'Year');
      setManualState({ modelEnabled: !!makeSel?.value, yearEnabled: false, canSearch: false });

      const make = makeSel?.value || '';
      const model = modelSel?.value || '';
      if (!make || !model) return;

      try {
        if (yearSel) { yearSel.disabled = true; fillSelect(yearSel, [], '…'); }
        await loadYears(make, model);
        setManualState({ modelEnabled: true, yearEnabled: true, canSearch: false });
        queueOpenNext('year');
      } catch {
        showManualMessage(root.dataset.textMsgLoadYearsError || 'Could not load years.', true, [modelSel]);
      }
    });

    yearSel?.addEventListener('change', () => {
      showManualMessage('');
      yearCombo?.syncToSelect();
      const ok = !!(makeSel?.value && modelSel?.value && yearSel?.value);
      setManualState({ modelEnabled: !!makeSel?.value, yearEnabled: !!modelSel?.value, canSearch: ok });
      if (ok) setTimeout(() => manualBtn?.focus(), 50);
    });

    manualBtn?.addEventListener('click', async () => {
      showManualMessage('');
      const make = makeSel?.value || '';
      const model = modelSel?.value || '';
      const year = yearSel?.value || '';
      if (!make || !model || !year) {
        const missing = [!make ? makeSel : null, !model ? modelSel : null, !year ? yearSel : null];
        showManualMessage(root.dataset.textMsgSelectManual || 'Select Make / Model / Year.', true, missing);
        return;
      }

      try {
        setLoading(manualBtn, true);
        const res = await apiPostJson('resolve', { make, model, year });

        // Normal case: UVC mapping exists
        if (res.status === 'ok' && res.bike) {
          await onBikeResolved(res.bike);
          return;
        }

        // Fallback case: no UVC mapping, but browsing should still work
        if (res.status === 'ok' && res.cart_payload) {
          const bike = {
            bike_uvc: '',
            bike_make: res.cart_payload.bike_make || make,
            bike_model: res.cart_payload.bike_model || model,
            bike_year: res.cart_payload.bike_year || year,
            clean_display_name:
              res.cart_payload.bike_clean_display_name || `${make} ${model} ${year}`
          };

          await onBikeResolved(bike);
          return;
        }

        // Real failure
        showManualMessage(root.dataset.textMsgNoManualMatch || 'No match found.', true);

      } catch {
        showManualMessage(root.dataset.textMsgManualError || 'Could not search bike. Try again.', true);
      } finally {
        setLoading(manualBtn, false);
      }
    });

    // Load makes + prefill from active bike
    try {
      await loadMakes();
      await prefillManualFromActiveBike();
      updateManualHelper();
    } catch {}
  }

  async function initBikeFinders() {
    const roots = qsa('[data-dr-bike-finder]');
    if (!roots.length) return;
    for (const root of roots) {
      // if we ever re-render sections, avoid double binding
      if (root.dataset.drbfBound === '1') continue;
      root.dataset.drbfBound = '1';
      await initOneBikeFinder(root);
    }
  }

  // -----------------------------
  // Garage modal
  // -----------------------------
  function openModal(modal) {
    if (!modal) return;
    if (modal.__drgCloseTimer) {
      window.clearTimeout(modal.__drgCloseTimer);
      modal.__drgCloseTimer = null;
    }
    modal.classList.add('is-open');
    requestAnimationFrame(() => modal.classList.add('is-visible'));
    modal.setAttribute('aria-hidden', 'false');
  }

  function closeModal(modal) {
    if (!modal) return;
    modal.classList.remove('is-visible');
    modal.setAttribute('aria-hidden', 'true');
    const ms = parseInt(getComputedStyle(modal).getPropertyValue('--dr-motion-duration')) || 220;
    modal.__drgCloseTimer = window.setTimeout(() => {
      modal.classList.remove('is-open');
      modal.__drgCloseTimer = null;
    }, ms);
  }

  function getGarageInitialFocus(modal) {
    const manualTab = qs('[data-tab="manual"]', modal);
    const isManual = !!manualTab && manualTab.classList.contains('is-active');
    if (isManual) return qs('[data-dr-combo="make"] .drbf__comboSearch', modal) || qs('[data-dr-combo="make"] .drbf__comboTrigger', modal) || qs('[data-make-select]', modal) || qs('[data-model-select]', modal) || qs('[data-year-select]', modal);
    return qs('[data-vrm-input]', modal) || qs('[data-tab="vrm"]', modal) || qs('[data-close-garage]', modal);
  }

  function trapGarageFocus(modal) {
    const container = qs('.drg__card', modal) || modal;
    const initialFocus = getGarageInitialFocus(modal);
    if (typeof trapFocus === 'function') {
      trapFocus(container, initialFocus || container);
    } else {
      initialFocus?.focus();
    }
  }

  function releaseGarageFocus(opener) {
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(opener || null);
      return;
    }
    opener?.focus?.();
  }

  function scrollToBikeFinderAndFocus() {
    const finder = qs('[data-dr-bike-finder]');
    if (finder) finder.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const input = qs('[data-vrm-input]', finder || document);
    setTimeout(() => input?.focus(), 250);
  }

  async function renderGarage(modal) {
    const listEl = qs('[data-garage-bikes-list]', modal);
    const emptyHint = qs('[data-garage-emptyhint]', modal);
    const savedSection = qs('[data-garage-saved-section]', modal);
    const changeBtn = qs('[data-garage-change]', modal);

    const { hasBike, bike } = await getActiveBike();
    const activeBikeKey = hasBike ? bikeIdentityKey(bike) : '';
    const garage = loadGarage();
    const activeSavedBike = garage.find(item => bikeIdentityKey(item) === activeBikeKey);
    const otherBikes = garage.filter(item => bikeIdentityKey(item) !== activeBikeKey);

    const activeListItem = hasBike ? { ...bike, _id: activeSavedBike?._id || '' } : null;

    // Change (if modal has inline panel, open it; else fall back to scroll)
    if (changeBtn && !changeBtn.dataset.bound) {
      changeBtn.dataset.bound = '1';
      changeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const mainPanel = qs('[data-garage-main]', modal);
        const changePanel = qs('[data-garage-change-panel]', modal);

        if (mainPanel && changePanel) {
          mainPanel.style.display = 'none';
          changePanel.style.display = '';
          // focus reg input inside modal finder
          const input = qs('[data-vrm-input]', changePanel);
          setTimeout(() => input?.focus(), 50);
          return;
        }

        // fallback behaviour
        const close = modal.__drCloseGarageModal;
        if (typeof close === 'function') close();
        else closeModal(modal);
        scrollToBikeFinderAndFocus();
      });
    }

    if (listEl) listEl.innerHTML = '';

    const totalRows = (activeListItem ? 1 : 0) + otherBikes.length;
    if (savedSection) savedSection.style.display = totalRows ? '' : 'none';

    if (!totalRows) {
      if (emptyHint) emptyHint.style.display = '';
      return;
    }
    if (emptyHint) emptyHint.style.display = 'none';

    if (activeListItem) {
      listEl.appendChild(buildGarageRow(activeListItem, modal, {
        isActive: true,
        onShop: async (activeBike) => {
          await applyBike(activeBike);

          if ((window.location.pathname || '').startsWith('/products/')) {
            if (isCurrentPdpBikeSpecific()) {
              window.location.reload();
            } else {
              const url = buildUrlWithBikeParams('/collections/all', activeBike.bike_make, activeBike.bike_model, activeBike.bike_year);
              window.location.assign(url);
            }
            return;
          }

          const basePath = resolveBikeRedirectPath();
          const url = buildUrlWithBikeParams(basePath, activeBike.bike_make, activeBike.bike_model, activeBike.bike_year);
          window.location.assign(url);
        }
      }));
    }

    otherBikes.forEach((item) => {
      listEl.appendChild(buildGarageRow(item, modal));
    });
  }

  async function initGarageModal() {
    const modal =
      qs('[data-dr-garage-source="popup-group"] [data-dr-garage-modal]') ||
      qs('[data-dr-garage-modal]');

    if (!modal) return;

    const closeBtn = qs('[data-close-garage]', modal);
    const overlay = qs('[data-garage-overlay]', modal);
    const card = qs('.drg__card', modal);
    let lastOpener = null;

    const mainPanel = qs('[data-garage-main]', modal);
    const changePanel = qs('[data-garage-change-panel]', modal);
    const backBtn = qs('[data-garage-change-back]', modal);

    card?.addEventListener('click', (e) => e.stopPropagation());

    // Back button (inline change panel)
    backBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      if (changePanel) changePanel.style.display = 'none';
      if (mainPanel) mainPanel.style.display = '';
    });

    // THEME-PROOF opener (capture phase)
    if (window.__drGarageOpenHandler) {
      document.removeEventListener('click', window.__drGarageOpenHandler, true);
    }

    window.__drGarageOpenHandler = async (e) => {
      const btn = e.target && e.target.closest ? e.target.closest('[data-open-garage], [data-dr-open-garage], [data-garage-open], a.header-my-garage, .header-my-garage, a[href="#my-garage"], a[href="#garage"]') : null;
      if (!btn) return;

      const liveModal = modal && modal.isConnected
        ? modal
        : (qs('[data-dr-garage-source="popup-group"] [data-dr-garage-modal]') || qs('[data-dr-garage-modal]'));
      if (!liveModal) return;

      e.preventDefault();

      // Always return to main view on open
      if (changePanel) changePanel.style.display = 'none';
      if (mainPanel) mainPanel.style.display = '';

      lastOpener = btn;
      await renderGarage(liveModal);
      openModal(liveModal);
      const modalFinder = liveModal.querySelector('[data-dr-bike-finder]');
      if (modalFinder && modalFinder.dataset.drbfBound !== '1') {
        modalFinder.dataset.drbfBound = '1';
        await initOneBikeFinder(modalFinder);
      }
      trapGarageFocus(liveModal);
    };

    document.addEventListener('click', window.__drGarageOpenHandler, true);

    if (!window.__drGarageEventBound) {
      window.__drGarageEventBound = true;
      document.addEventListener('dr:openGarage', (e) => {
        e.preventDefault();
        const opener = document.querySelector('[data-open-garage], [data-dr-open-garage], [data-garage-open], a.header-my-garage, .header-my-garage, a[href="#my-garage"], a[href="#garage"]');
        if (opener) {
          opener.click();
        }
      });
    }

    function closeGarageModal() {
      const isOpen = modal.classList.contains('is-open') && modal.getAttribute('aria-hidden') !== 'true';
      if (!isOpen) return;
      closeModal(modal);
      releaseGarageFocus(lastOpener);
    }

    modal.__drCloseGarageModal = closeGarageModal;

    closeBtn?.addEventListener('click', () => closeGarageModal());
    overlay?.addEventListener('click', () => closeGarageModal());

    if (!window.__drGarageEscBound) {
      window.__drGarageEscBound = true;
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const modal =
          qs('[data-dr-garage-source="popup-group"] [data-dr-garage-modal]') ||
          qs('[data-dr-garage-modal]');
        if (!garageModal) return;
        const isOpen = garageModal.classList.contains('is-open') && garageModal.getAttribute('aria-hidden') !== 'true';
        if (!isOpen) return;
        const close = garageModal.__drCloseGarageModal;
        if (typeof close === 'function') close();
      });
    }
  }

  // -----------------------------
  // Collection auto-apply (unless excluded)
  // -----------------------------
  async function initCollectionAutoFilter() {
    const path = window.location.pathname || '';
    if (!path.startsWith('/collections/')) return;
    if (isCollectionExcludedFromBikeFiltering()) return;

    const url = new URL(window.location.href);
    if (hasRbBikeFilters(url)) return;

    const { hasBike, bike } = await getActiveBike();
    if (!hasBike) return;

    const next = buildUrlWithBikeParams(url.pathname, bike.bike_make, bike.bike_model, bike.bike_year);
    window.location.replace(next);
  }

    // -----------------------------
    // Featured collection: filter all-best-sellers by active bike UVC
    // -----------------------------
    async function initBestSellersFeaturedCollectionFilter() {
      const sections = document.querySelectorAll('[data-dr-best-sellers-section="true"]');
      if (!sections.length) return;
      const debugFit = window.DR_DEBUG_FIT === true;

      const hideSection = (sec) => {
        const sectionWrapper = sec.closest('.shopify-section');
        if (sectionWrapper) {
          sectionWrapper.setAttribute('hidden', 'hidden');
          sectionWrapper.setAttribute('aria-hidden', 'true');
          return;
        }
        sec.style.display = 'none';
      };

      const showSection = (sec) => {
        const sectionWrapper = sec.closest('.shopify-section');
        if (sectionWrapper) {
          sectionWrapper.removeAttribute('hidden');
          sectionWrapper.removeAttribute('aria-hidden');
        }
        sec.style.display = '';
      };

      if (debugFit) {
        console.log('[DR fit debug] best-sellers sections found:', sections.length);
      }

      let active;
      try {
        active = await getActiveBike(); // uses cart attributes
      } catch {
        // If we can’t read cart, safest is hide the section
        sections.forEach(hideSection);
        return;
      }

      if (!active?.hasBike) {
        // Only show this grid when a bike is set
        sections.forEach(hideSection);
        if (debugFit) {
          console.log('[DR fit debug] no active bike; sections hidden');
        }
        return;
      }

      const activeBike = buildActiveBikeKey(active.bike);
      if (!activeBike) {
        sections.forEach(hideSection);
        if (debugFit) {
          console.log('[DR fit debug] active bike key missing; sections hidden');
        }
        return;
      }

      if (debugFit) {
        console.log('[DR fit debug] active bike state:', {
          hasBike: active.hasBike,
          activeBike
        });
      }

      sections.forEach(sec => {
        showSection(sec);

        // Filter items
        const cards = sec.querySelectorAll('[data-dr-best-sellers-item], li, .product-card, .grid__item');

        let kept = 0;

        cards.forEach(card => {
          // Find the element that actually contains fitment datasets (badge span, wrapper, etc.)
          const metaEl =
            card.matches('[data-compatible-bikes]') ? card :
            card.querySelector('[data-compatible-bikes]');

          // If we can't find compatibility data on this card, fail-closed for bike-specific items.
          const bikeSpecific = String(metaEl?.dataset?.drBikeSpecific) === 'true';
          const compatibleBikes = metaEl ? getCompatibleBikesForElement(metaEl, 'compatibleBikes') : [];

          let ok = true;

          if (bikeSpecific) {
            ok = compatibleBikes.length ? matchesCompatibleBikes(activeBike, compatibleBikes) : false;
          }

          // Hide/show the whole card
          card.style.display = ok ? '' : 'none';
          if (ok) kept++;
        });

        // Hide whole section if nothing matches
        if (kept === 0) {
          hideSection(sec);
        } else {
          showSection(sec);
        }

        if (debugFit) {
          console.log('[DR fit debug] best-sellers section filtered:', {
            items: cards.length,
            kept,
            sectionState: kept === 0 ? 'hidden' : 'shown'
          });
        }
      });
    }


  // -----------------------------
  // Boot
  // -----------------------------
  document.addEventListener('DOMContentLoaded', () => {
    const popupGroupModal = qs('[data-dr-garage-source="popup-group"] [data-dr-garage-modal]');
    const fallbackGarage = qs('[data-dr-garage-source="fallback"]');
    if (popupGroupModal && fallbackGarage) fallbackGarage.remove();

    updateGarageCountBubbles();
    hideBikeFinderIfExcluded();

    initBikeFinders().catch(() => {});
    initGarageModal().catch(() => {});
    initCollectionAutoFilter().catch(() => {});
    initFitmentBanner().catch(() => {});
    initPdpFitmentConfirm().catch(() => {});
    initPdpFitBadge().catch(() => {});
    initBestSellersFeaturedCollectionFilter().catch(() => {});
    initHomeFitmentConfirm().catch(() => {});

    document.addEventListener('dr:bikeChanged', () => {
      initHomeFitmentConfirm().catch(() => {});
      initPdpFitmentConfirm().catch(() => {});
      initPdpFitBadge().catch(() => {});
      initBestSellersFeaturedCollectionFilter().catch(() => {});
    });

    document.addEventListener('shopify:section:load', () => {
      updateGarageCountBubbles();
    });

    document.addEventListener('click', (event) => {
      if (!event.target || !event.target.closest) return;
      if (event.target.closest('[data-open-garage], .header__icon--menu')) {
        updateGarageCountBubbles();
      }
    });
  });
})();